import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Stack,
} from '@mui/material';
import {
  TrendingUp,
  Speed,
  Category,
  Extension,
  People,
  CreditCard,
  Timeline,
} from '@mui/icons-material';
import { api } from '../services/api';
import { Metrics } from '../types';
import { useConfigStore } from '../stores/useConfigStore';
import { getOrderedEntityKeys } from '../utils/entityOrder';
import { getStageHexColor } from '../utils/stageColors';

/**
 * Metrics Page
 * Deep dive analytics with trends, cohorts, and funnel analysis
 */
export const MetricsPage: React.FC = () => {
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { config, loadConfig } = useConfigStore();

  useEffect(() => {
    loadMetrics();
    loadConfig();
    // loadMetrics is a stable local async fn; loadConfig is a stable store action
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadConfig]);

  const loadMetrics = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.getMetrics();
      setMetrics(data);
    } catch (err) {
      setError('Failed to load metrics. Please try again.');
      console.error('Error loading metrics:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom fontWeight={600}>
          Metrics & Analytics
        </Typography>
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!metrics) {
    return null;
  }

  return (
    <Box>
      <Box mb={4}>
        <Typography variant="h4" fontWeight={600}>
          Metrics & Analytics
        </Typography>
        <Typography variant="body2" color="text.secondary" mt={0.5}>
          Deep dive into customer health trends, onboarding performance, and feature adoption
        </Typography>
      </Box>

      <Stack spacing={3}>
        {/* Health Score Distribution */}
        {metrics.healthScoreDistribution && (
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Box display="flex" alignItems="center" gap={1} mb={3}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  bgcolor: 'primary.light',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <TrendingUp sx={{ color: 'secondary.main', fontSize: 20 }} />
              </Box>
              <Typography variant="h6" fontWeight={600}>
                Health Score Distribution
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                gap: 2,
              }}
            >
              <Box>
                <Typography variant="h4" fontWeight={600} color="success.main">
                  {metrics.healthScoreDistribution.healthy}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Healthy (75-100)
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {metrics.totalAccounts > 0
                    ? `${((metrics.healthScoreDistribution.healthy / metrics.totalAccounts) * 100).toFixed(1)}%`
                    : '0%'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="h4" fontWeight={600} sx={{ color: '#2196f3' }}>
                  {metrics.healthScoreDistribution.moderate}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Moderate (50-74)
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {metrics.totalAccounts > 0
                    ? `${((metrics.healthScoreDistribution.moderate / metrics.totalAccounts) * 100).toFixed(1)}%`
                    : '0%'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="h4" fontWeight={600} color="warning.main">
                  {metrics.healthScoreDistribution.atRisk}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  At Risk (25-49)
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {metrics.totalAccounts > 0
                    ? `${((metrics.healthScoreDistribution.atRisk / metrics.totalAccounts) * 100).toFixed(1)}%`
                    : '0%'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="h4" fontWeight={600} color="error.main">
                  {metrics.healthScoreDistribution.critical}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Critical (0-24)
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {metrics.totalAccounts > 0
                    ? `${((metrics.healthScoreDistribution.critical / metrics.totalAccounts) * 100).toFixed(1)}%`
                    : '0%'}
                </Typography>
              </Box>
            </Box>
          </Paper>
        )}

        {/* Time to Value */}
        {metrics.timeToValue && (
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Box display="flex" alignItems="center" gap={1} mb={3}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  bgcolor: 'primary.light',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Speed sx={{ color: 'secondary.main', fontSize: 20 }} />
              </Box>
              <Typography variant="h6" fontWeight={600}>
                Time to Value (Onboarding Speed)
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: 3,
              }}
            >
              <Card sx={{ bgcolor: 'primary.light', borderRadius: 2, boxShadow: 'none' }}>
                <CardContent>
                  <Typography variant="caption" color="text.secondary" textTransform="uppercase">
                    Avg Time to First Entity
                  </Typography>
                  <Typography variant="h3" fontWeight={600} color="secondary.main">
                    {metrics.timeToValue.avgTTFFirstEntity}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    days
                  </Typography>
                </CardContent>
              </Card>
              <Box>
                <Typography variant="body2" fontWeight={600} mb={2}>
                  Which Entity Created First?
                </Typography>
                {Object.entries(metrics.timeToValue.firstEntityTypeDistribution)
                  .sort((a, b) => b[1] - a[1])
                  .slice(0, 5)
                  .map(([entityType, count]) => (
                    <Box key={entityType} mb={1}>
                      <Box display="flex" justifyContent="space-between" mb={0.5}>
                        <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                          {entityType}
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {count}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          width: '100%',
                          height: 6,
                          bgcolor: 'grey.200',
                          borderRadius: 1,
                          overflow: 'hidden',
                        }}
                      >
                        <Box
                          sx={{
                            width: `${metrics.totalAccounts > 0 ? (count / metrics.totalAccounts) * 100 : 0}%`,
                            height: '100%',
                            bgcolor: 'secondary.main',
                          }}
                        />
                      </Box>
                    </Box>
                  ))}
              </Box>
            </Box>
          </Paper>
        )}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 3,
          }}
        >
          {/* Cohort Analysis */}
          {metrics.cohorts && (
            <Paper sx={{ p: 3, borderRadius: 2 }}>
              <Box display="flex" alignItems="center" gap={1} mb={3}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    bgcolor: 'primary.light',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Timeline sx={{ color: 'secondary.main', fontSize: 20 }} />
                </Box>
                <Typography variant="h6" fontWeight={600}>
                  Account Cohorts (by Age)
                </Typography>
              </Box>
              <Stack spacing={2}>
                <Box>
                  <Box display="flex" justifyContent="space-between" mb={0.5}>
                    <Typography variant="body2">New (0-7 days)</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {metrics.cohorts.new}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: '100%',
                      height: 8,
                      bgcolor: 'grey.200',
                      borderRadius: 1,
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      sx={{
                        width: `${metrics.totalAccounts > 0 ? (metrics.cohorts.new / metrics.totalAccounts) * 100 : 0}%`,
                        height: '100%',
                        bgcolor: 'success.main',
                      }}
                    />
                  </Box>
                </Box>
                <Box>
                  <Box display="flex" justifyContent="space-between" mb={0.5}>
                    <Typography variant="body2">Recent (8-30 days)</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {metrics.cohorts.recent}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: '100%',
                      height: 8,
                      bgcolor: 'grey.200',
                      borderRadius: 1,
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      sx={{
                        width: `${metrics.totalAccounts > 0 ? (metrics.cohorts.recent / metrics.totalAccounts) * 100 : 0}%`,
                        height: '100%',
                        bgcolor: 'primary.main',
                      }}
                    />
                  </Box>
                </Box>
                <Box>
                  <Box display="flex" justifyContent="space-between" mb={0.5}>
                    <Typography variant="body2">Established (31-90 days)</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {metrics.cohorts.established}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: '100%',
                      height: 8,
                      bgcolor: 'grey.200',
                      borderRadius: 1,
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      sx={{
                        width: `${metrics.totalAccounts > 0 ? (metrics.cohorts.established / metrics.totalAccounts) * 100 : 0}%`,
                        height: '100%',
                        bgcolor: 'warning.main',
                      }}
                    />
                  </Box>
                </Box>
                <Box>
                  <Box display="flex" justifyContent="space-between" mb={0.5}>
                    <Typography variant="body2">Mature (90+ days)</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {metrics.cohorts.mature}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: '100%',
                      height: 8,
                      bgcolor: 'grey.200',
                      borderRadius: 1,
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      sx={{
                        width: `${metrics.totalAccounts > 0 ? (metrics.cohorts.mature / metrics.totalAccounts) * 100 : 0}%`,
                        height: '100%',
                        bgcolor: 'secondary.main',
                      }}
                    />
                  </Box>
                </Box>
              </Stack>
            </Paper>
          )}

          {/* Subscription Distribution */}
          {metrics.subscriptionDistribution && (
            <Paper sx={{ p: 3, borderRadius: 2 }}>
              <Box display="flex" alignItems="center" gap={1} mb={3}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    bgcolor: 'primary.light',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CreditCard sx={{ color: 'secondary.main', fontSize: 20 }} />
                </Box>
                <Typography variant="h6" fontWeight={600}>
                  Subscription Status
                </Typography>
              </Box>
              <Stack spacing={2}>
                {Object.entries(metrics.subscriptionDistribution)
                  .sort((a, b) => b[1] - a[1])
                  .map(([status, count]) => (
                    <Box key={status}>
                      <Box display="flex" justifyContent="space-between" mb={0.5}>
                        <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                          {status.replace(/_/g, ' ')}
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {count}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          width: '100%',
                          height: 8,
                          bgcolor: 'grey.200',
                          borderRadius: 1,
                          overflow: 'hidden',
                        }}
                      >
                        <Box
                          sx={{
                            width: `${metrics.totalAccounts > 0 ? (count / metrics.totalAccounts) * 100 : 0}%`,
                            height: '100%',
                            bgcolor: 'primary.main',
                          }}
                        />
                      </Box>
                    </Box>
                  ))}
              </Stack>
            </Paper>
          )}
        </Box>

        {/* Onboarding Funnel */}
        {metrics.funnel && Array.isArray(metrics.funnel) && metrics.funnel.length > 0 && (
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Box display="flex" alignItems="center" gap={1} mb={3}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  bgcolor: 'primary.light',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <People sx={{ color: 'secondary.main', fontSize: 20 }} />
              </Box>
              <Typography variant="h6" fontWeight={600}>
                Onboarding Funnel
              </Typography>
            </Box>
            <Stack spacing={1}>
              {metrics.funnel.map((stage, index) => {
                // Calculate percentage of total accounts that reached this stage
                const reachedPercentage = metrics.totalAccounts > 0 ? (stage.count / metrics.totalAccounts) * 100 : 0;

                return (
                  <Box key={stage.stage}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                      <Typography variant="body2" fontWeight={500} sx={{ textTransform: 'capitalize' }}>
                        {stage.displayName}
                      </Typography>
                      <Box display="flex" gap={2} alignItems="center">
                        <Typography variant="body2" fontWeight={600}>
                          {stage.count}
                        </Typography>
                        <Typography
                          variant="caption"
                          color={reachedPercentage >= 70 ? 'success.main' : reachedPercentage >= 50 ? 'warning.main' : 'error.main'}
                        >
                          {reachedPercentage.toFixed(1)}% of total
                        </Typography>
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        width: '100%',
                        height: 10,
                        bgcolor: 'grey.200',
                        borderRadius: 1,
                        overflow: 'hidden',
                      }}
                    >
                      <Box
                        sx={{
                          width: `${reachedPercentage}%`,
                          height: '100%',
                          bgcolor: getStageHexColor(stage.stage, config?.stageConfig),
                          transition: 'width 0.3s ease',
                        }}
                      />
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          </Paper>
        )}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 3,
          }}
        >
          {/* Entity Counts */}
          {metrics.entityCounts && Object.keys(metrics.entityCounts).length > 0 && (
            <Paper sx={{ p: 3, borderRadius: 2 }}>
              <Box display="flex" alignItems="center" gap={1} mb={3}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    bgcolor: 'primary.light',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Category sx={{ color: 'secondary.main', fontSize: 20 }} />
                </Box>
                <Typography variant="h6" fontWeight={600}>
                  Total Entity Activity
                </Typography>
              </Box>
              <Stack spacing={2}>
                {getOrderedEntityKeys(config?.entityDefinitions, Object.keys(metrics.entityCounts))
                  .slice(0, 10)
                  .map((entityType) => [entityType, metrics.entityCounts![entityType]] as [string, number])
                  .filter(([, count]) => count != null)
                  .map(([entityType, count]) => (
                    <Box key={entityType}>
                      <Box display="flex" justifyContent="space-between" mb={0.5}>
                        <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                          {entityType}
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {count.toLocaleString()}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
              </Stack>
            </Paper>
          )}

          {/* Feature Adoption */}
          {metrics.featureAdoption && Object.keys(metrics.featureAdoption).length > 0 && (
            <Paper sx={{ p: 3, borderRadius: 2 }}>
              <Box display="flex" alignItems="center" gap={1} mb={3}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    bgcolor: 'primary.light',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Extension sx={{ color: 'secondary.main', fontSize: 20 }} />
                </Box>
                <Typography variant="h6" fontWeight={600}>
                  Feature Adoption Rates
                </Typography>
              </Box>
              <Stack spacing={2}>
                {Object.entries(metrics.featureAdoption)
                  .sort((a, b) => b[1].percentage - a[1].percentage)
                  .map(([featureName, data]) => (
                    <Box key={featureName}>
                      <Box display="flex" justifyContent="space-between" mb={0.5}>
                        <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                          {featureName.replace(/([A-Z])/g, ' $1').trim()}
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {data.adopted} ({data.percentage.toFixed(1)}%)
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          width: '100%',
                          height: 6,
                          bgcolor: 'grey.200',
                          borderRadius: 1,
                          overflow: 'hidden',
                        }}
                      >
                        <Box
                          sx={{
                            width: `${data.percentage}%`,
                            height: '100%',
                            bgcolor: data.percentage >= 75 ? 'success.main' : data.percentage >= 50 ? 'primary.main' : 'warning.main',
                          }}
                        />
                      </Box>
                    </Box>
                  ))}
              </Stack>
            </Paper>
          )}
        </Box>
      </Stack>
    </Box>
  );
};
